import React from 'react';
import PropTypes from 'prop-types';
import { LuBoxes, LuChevronDown, LuPlus, LuTrash2 } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import IconButton from '../../../components/ui/IconButton';
import { INPUT_CLASS } from '../../../components/form/FormControls';
import { getCargoDirection } from '../../../utils/companyShipments';
import useCargoText from './useCargoText';

const SELECT_CLASS = `${INPUT_CLASS} tw-cursor-pointer tw-appearance-none tw-pr-9 [&>option]:tw-bg-white [&>option]:tw-text-slate-800 dark:[&>option]:tw-bg-[#1f232b] dark:[&>option]:tw-text-slate-100`;

const emptyItem = () => ({ stockId: '', stockName: '', quantity: '' });

/**
 * Kargoya bağlı stok kalemleri. Kalemler, kargo ilgili duruma geldiğinde
 * stoğa otomatik işlenir (bkz. utils/companyShipments.js).
 */
const CargoItemsField = ({ items, onChange, stocks, direction, error }) => {
  const { label } = useCargoText();

  const updateItem = (index, patch) =>
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const handleStockChange = (index, stockId) => {
    const stock = stocks.find((option) => option.id === stockId);
    updateItem(index, { stockId, stockName: stock?.name || '' });
  };

  return (
    <div className="tw-space-y-3">
      <p className="tw-flex tw-items-start tw-gap-2 tw-rounded-xl tw-bg-sky-500/[0.07] tw-px-3 tw-py-2.5 tw-text-xs tw-leading-relaxed tw-text-sky-900 dark:tw-text-sky-100">
        <LuBoxes aria-hidden="true" className="tw-mt-px tw-h-4 tw-w-4 tw-shrink-0 tw-text-sky-500" />
        {label(`items.hint.${getCargoDirection(direction).value}`)}
      </p>

      {items.length === 0 && (
        <p className="tw-px-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('items.empty')}</p>
      )}

      {items.map((item, index) => {
        const stock = stocks.find((option) => option.id === item.stockId);
        return (
          // Satırların kalıcı kimliği yok; sıra yalnızca ekleme/silmeyle değişir
          <div key={index} className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-relative tw-block tw-min-w-0 tw-flex-1">
              <select
                className={SELECT_CLASS}
                value={item.stockId}
                onChange={(e) => handleStockChange(index, e.target.value)}
                aria-label={label('items.stock')}
              >
                <option value="">{label('items.stockPlaceholder')}</option>
                {/* Kayıtlı kalemin stok kartı sonradan pasifleşmiş/silinmiş olabilir */}
                {item.stockId && !stock && <option value={item.stockId}>{item.stockName}</option>}
                {stocks.map((option) => (
                  <option key={option.id} value={option.id}>
                    {label('items.option', { name: option.name, remaining: option.remaining })}
                  </option>
                ))}
              </select>
              <LuChevronDown aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
            </span>
            {/* INPUT_CLASS tam genişlik verir; genişliği saran kutu belirler */}
            <span className="tw-block tw-w-24 tw-shrink-0 sm:tw-w-28">
              <input
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                className={`${INPUT_CLASS} tw-tabular-nums`}
                value={item.quantity}
                onChange={(e) => updateItem(index, { quantity: e.target.value })}
                placeholder={label('items.quantity')}
                aria-label={label('items.quantity')}
              />
            </span>
            <IconButton label={label('items.remove')} tone="alert" onClick={() => onChange(items.filter((_, i) => i !== index))}>
              <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
            </IconButton>
          </div>
        );
      })}

      {error && <p role="alert" className="tw-px-0.5 tw-text-xs tw-font-medium tw-text-rose-600 dark:tw-text-rose-400">{error}</p>}

      <Button size="sm" variant="secondary" onClick={() => onChange([...items, emptyItem()])}>
        <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
        {label('items.add')}
      </Button>
    </div>
  );
};

CargoItemsField.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  stocks: PropTypes.arrayOf(PropTypes.object).isRequired,
  direction: PropTypes.string,
  error: PropTypes.string,
};

export default CargoItemsField;
